import React, { useState } from 'react';
import { Wrench, Hash, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import api from '../api/client';

// Phase 33.1 — Admin → Settings → Maintenance.
// One-shot, admin-triggered backfill of `user_number` for legacy users.
// The actual logic lives in POST /api/admin/maintenance/backfill-user-numbers
// and is idempotent — re-running is always safe (no-op when all users are
// already numbered).

const MaintenancePanel = () => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const runBackfill = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/admin/maintenance/backfill-user-numbers');
      setResult(res.data);
      if (res.data?.updated_count > 0) {
        toast({
          title: 'Backfill completato',
          description: `Assegnati ${res.data.updated_count} nuovi ID Nymia (${res.data.first_assigned}→${res.data.last_assigned}).`,
        });
      } else {
        toast({
          title: 'Nessun aggiornamento necessario',
          description: 'Tutti gli utenti hanno già un ID Nymia.',
        });
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Backfill fallito. Riprova.';
      setError(msg);
      toast({ title: 'Errore backfill', description: msg, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="mt-6 rounded-2xl border border-border bg-card p-6"
      data-testid="maintenance-panel"
    >
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/20 gold-text">
          <Wrench className="h-4 w-4" />
        </span>
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide gold-text">Maintenance</h3>
          <p className="text-[11px] text-muted-foreground">Operazioni una-tantum per la gestione dei dati legacy.</p>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-background p-4">
        <div className="flex items-start gap-3">
          <Hash className="mt-0.5 h-5 w-5 gold-text shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold">Assegna ID Nymia mancanti</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Assegna un <code className="rounded bg-muted px-1 py-0.5 text-[11px]">user_number</code> sequenziale
              (partendo da 1001) a tutti gli utenti già registrati che ne sono sprovvisti, in ordine cronologico di
              registrazione. Operazione <strong>idempotente</strong>: rilanciarla non duplica né rinumera.
            </p>
            <Button
              onClick={runBackfill}
              disabled={busy}
              className="mt-3 h-9 gold-gradient text-xs"
              data-testid="maintenance-backfill-btn"
            >
              {busy ? (
                <span className="inline-flex items-center gap-1.5">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> In corso…
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <Hash className="h-3.5 w-3.5" /> Assegna ID Nymia mancanti
                </span>
              )}
            </Button>
          </div>
        </div>

        {result && (
          <div
            className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-xs"
            data-testid="maintenance-backfill-result"
          >
            <div className="mb-2 flex items-center gap-1.5 font-semibold text-emerald-300">
              <CheckCircle2 className="h-4 w-4" /> Operazione completata
            </div>
            <ul className="grid grid-cols-1 gap-1 tabular-nums sm:grid-cols-2">
              <li><span className="text-muted-foreground">Utenti aggiornati:</span> <strong>{result.updated_count}</strong></li>
              <li><span className="text-muted-foreground">Primo ID assegnato:</span> <strong>{result.first_assigned ?? '—'}</strong></li>
              <li><span className="text-muted-foreground">Ultimo ID assegnato:</span> <strong>{result.last_assigned ?? '—'}</strong></li>
              <li><span className="text-muted-foreground">Contatore attuale:</span> <strong>{result.counter_value}</strong></li>
              <li><span className="text-muted-foreground">Prossima registrazione:</span> <strong>{result.next_register_will_be}</strong></li>
              <li><span className="text-muted-foreground">Utenti totali:</span> <strong>{result.total_users}</strong></li>
              <li><span className="text-muted-foreground">Con ID Nymia:</span> <strong>{result.users_with_number} / {result.total_users}</strong></li>
              <li><span className="text-muted-foreground">Idempotente:</span> <strong>{result.idempotent ? 'sì' : 'no'}</strong></li>
            </ul>
            <p className="mt-2 italic text-muted-foreground">{result.message}</p>
          </div>
        )}

        {error && (
          <div
            className="mt-4 flex items-start gap-2 rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-xs text-red-300"
            data-testid="maintenance-backfill-error"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4" />
            <span>{error}</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default MaintenancePanel;
